import React, { useContext, useMemo, useState } from 'react';
import { WebMediaFolder } from '../../graphql/entities';
import * as QueryHelper from '../../utils/QueryHelper';
import { useMutation, useQuery } from '@apollo/client';
import WebMediaFolderListQuery from '../../graphql/queries/web/WebMediaFolderListQuery.graphql';
import WebMediaFileListQuery from '../../graphql/queries/web/WebMediaFileListQuery.graphql';
import { PrimaryButton, ProgressIndicator } from '@fluentui/react';
import CheckboxTree from '../../components/connectedForm/CheckboxTree';
import WebMediaFolderForm, { FileDeleteConfirmationModal, MediaFileEntry, MediaFileList } from './WebMediaFolderForm';
import { BasicHeader } from '../../actions/BasicHeader';
import Fieldset from '../../components/connectedForm/Fieldset';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { Button, DangerButton, SuccessButton } from '../../components/connectedForm/Button';
import { default as Form } from '../../components/connectedForm';
import { BlurTextField } from '../../components/connectedForm/inputComponents';
import { WebMediaFolderMutation } from '../../graphql/mutations/web/WebMediaFolderMutation.graphql';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import getClientMutationId from '../../utils/getClientMutationId';
import { serviceActions } from '../../state/modules/service';
import {
  AlertType,
  IWebMediaFolderMutationResult,
  IWebMediaFolderMutationVariables,
  IWebMediaFoldersQueryResult,
  IWebMediaFileQueryResult,
  FilterOperation,
  IWebMediaFile,
  IWebMediaFileEdge,
} from '../../graphql/schema/p4types';
import { useDispatch } from 'react-redux';
import AppContext from '../../components/Context/AppContext';
import { HoldingMessage } from '../../components/FeatureFlag/HoldingMessage';
import { WebCMSHoldingInfo } from '../../components/FeatureFlag/WebCMSHoldingInfo';
import { BlurTextFieldType } from '../../components/connectedForm/BlurTextField/BlurTextField';
import { SearchContainer, SearchInput } from '../../components/NavMenu/NavMenu';
import { useDebouncedState } from '../salesLedger/tradeCounter/hooks';
import HeaderContainer from '../../components/connectedForm/HeaderContainer';
import Heading2 from '../../components/connectedForm/Heading2';
import HeaderEntityContainer from '../../components/connectedForm/HeaderEntityContainer';
import HeaderEntityValue from '../../components/connectedForm/HeaderEntityValue';
import HeaderAction from '../../components/connectedForm/HeaderAction';
import WebMediaFileMutation from '../../graphql/mutations/web/WebMediaFileMutation.graphql';
import { Maybe } from 'graphql/jsutils/Maybe';
import FluentModal from '../../components/modals/FluentModal';

const WebMediaFolderTreespace = (props: {
  children?: any;
  allowInsert?: boolean;
  onInsertClick?: (value: string) => void;
}) => {
  const { fullUserDetails } = useContext(AppContext);

  const [categories, setCategories] = useState<IWebMediaFoldersQueryResult>();
  const [selectedNode, setSelectedNode] = useState<string>('');
  const [isRoot, setIsRoot] = useState<boolean>(false);
  const [newCategory, setNewCategory] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<Array<any>>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [searchTerm, debouncedSearchTerm, setSearchTerm] = useDebouncedState<Maybe<string>>('', 500);
  const [searchResults, setSearchResults] = useState<Array<IWebMediaFileEdge>>([]);
  const [activeFile, setActiveFile] = useState<Maybe<IWebMediaFile>>(null);
  const [mediaFileModalOpen, setMediaFileModalOpen] = useState<boolean>(false);

  const handleQueryError = useComponentQueryErrorHandling();
  const dispatch = useDispatch();

  const [webMediaFileMutation] = useMutation(WebMediaFileMutation, {
    onError: handleQueryError,
  });

  const [webMediaFolderMutation] = useMutation<IWebMediaFolderMutationResult, IWebMediaFolderMutationVariables>(
    WebMediaFolderMutation,
    {
      onError: handleQueryError,
    }
  );

  if (!fullUserDetails?.flags?.webCMSEnabled) {
    return <HoldingMessage {...WebCMSHoldingInfo} />;
  }

  const constructNodes = (data: Array<any>, root = null) => {
    const rootNodes: any[] = [];
    data.forEach((values) => {
      if (values.parent === root || (values.parent !== null && values.parent.id === root)) {
        rootNodes.push({
          value: values.id.toString(),
          rootCategory: 1,
          label: values.name,
          showCheckbox: false,
          children: constructNodes(data, values.id),
          className: values.id.toString() === selectedNode ? 'activeNode' : '',
        });
      }
    });
    return rootNodes;
  };

  const { loading, refetch: refetchCategories } = useQuery<IWebMediaFoldersQueryResult>(WebMediaFolderListQuery, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted(data: IWebMediaFoldersQueryResult) {
      const processedCategories = QueryHelper.processData(data);
      const searchForSelectedNode = processedCategories.find((node) => {
        return node.id.toString() === selectedNode;
      });
      if (searchForSelectedNode === undefined) {
        setSelectedNode(processedCategories[0]?.id.toString());
      }
      setCategories(data);
    },
  });

  const { refetch: refetchSearchResults } = useQuery<IWebMediaFileQueryResult>(WebMediaFileListQuery, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !debouncedSearchTerm || !debouncedSearchTerm?.trim(),
    variables: {
      orderBy: 'fileName',
      orderDirection: 'DESC',
      filters: [
        {
          filterOperation: FilterOperation.Contains,
          filterParameters: {
            field: 'fileName',
            value: debouncedSearchTerm,
          },
        },
      ],
    },
    onCompleted(data: IWebMediaFileQueryResult) {
      setSearchResults(data?.list?.edges ?? []);
      setSelectedNode('');
    },
  });

  const categoryNodes = useMemo(() => {
    if (categories) {
      return constructNodes(QueryHelper.processData(categories));
    }
    return [];
  }, [categories, selectedNode]);

  if (!categories) {
    if (loading) {
      return <ProgressIndicator label="Loading" description="Web Attachment Categories Data" />;
    }
  }

  const onExpand = (expanded: Array<any>) => {
    setExpanded(expanded);
  };

  const onNodeClick = (targetNode: { value: any; toString: () => any }) => {
    const nodeId = targetNode.value ?? targetNode.toString();
    setSearchTerm(null);
    setSelectedNode(nodeId);
    setNewCategory(false);
    setIsRoot(false);
  };

  const onNewFolderClick = () => {
    webMediaFolderMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          webMediaFolder: {
            name: newFolderName,
            parent: {
              id: !isRoot ? selectedNode : null,
            },
            root: isRoot,
          },
        },
      },
    }).then(() => {
      refetchCategories();
      setNewFolderName('');
      setModalOpen(false);
    });
  };

  const onAddRootCategoryClick = () => {
    setNewFolderName('');
    setIsRoot(true);
    setModalOpen(true);
  };

  const onDeleteCategoryClick = () => {
    dispatch(
      serviceActions.genericAlert(
        'Confirm Delete',
        `Do you want to delete this folder and all of the files within it?`,
        AlertType.Error,
        {
          alertField: 'delete',
          alertForm: WebMediaFolder.type,
          status: 'CONFIRMATION_REQUIRED',
          confirmationReference: 'deleted',
        }
      )
    );
  };
  const onClick = (mediaFile: any) => {
    setActiveFile(mediaFile);
  };

  const handleDeleteClick = () => {
    setMediaFileModalOpen(true);
  };

  const handleDeleteFile = () => {
    webMediaFileMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          webMediaFile: {
            id: activeFile?.id,
            deleted: true,
          },
        },
      },
    }).then(() => {
      setMediaFileModalOpen(false);
      setActiveFile(null);
      refetchSearchResults();
    });
  };

  return (
    <div className={`treespaceContainer ${props.allowInsert ? 'treespaceContainerModal' : ''}`}>
      {props.children}
      <div className={`react-grid-List treespaceSidebar ${props.allowInsert ? 'treespaceSidebarModal' : ''}`}>
        <BlurTextField
          style={{ marginBottom: '10px' }}
          data-testid="searchTerm"
          label="Search"
          type={BlurTextFieldType.Text}
          value={searchTerm}
          onChange={(value: string) => {
            setSearchTerm(value);
          }}
          enableOnChange
          clearButton={true}
        />
        <PrimaryButton
          className="btnMarginBottom btnMarginTop btnMarginRight"
          data-testid="productCategories-addRoot"
          onClick={onAddRootCategoryClick}
        >
          Add Folder
        </PrimaryButton>
        {categories?.list.count > 0 ? (
          <>
            <DangerButton
              className="btnMarginBottom"
              style={{ margin: '5px 0px 15px 0px', padding: '0px', display: 'block' }}
              onClick={onDeleteCategoryClick}
            >
              Delete Folder
            </DangerButton>
          </>
        ) : null}
        <CheckboxTree
          nodes={categoryNodes}
          expanded={expanded}
          onExpand={onExpand}
          onClick={onNodeClick}
          noCascade={true}
          showExpandAll={true}
        />
      </div>
      <div className="treespaceContent">
        {!debouncedSearchTerm && (
          <>
            {selectedNode && !newCategory ? (
              <WebMediaFolderForm
                overrideParams={{
                  id: newCategory ? 'New' : selectedNode,
                  parentId: newCategory && isRoot ? null : selectedNode,
                  isRoot: isRoot,
                  webMediaFolder: WebMediaFolder.title,
                }}
                refetchCategories={refetchCategories}
                setSelectedNode={onNodeClick}
                defaultSaveOption={{ value: 'CONTINUE' }}
                allowInsert={props.allowInsert}
                onInsertClick={props.onInsertClick}
              />
            ) : (
              <div key="no-images-upload-link" style={{ display: 'block', margin: '20px auto', textAlign: 'center' }}>
                <p>Please add a folder to start uploading files</p>
              </div>
            )}
          </>
        )}

        {debouncedSearchTerm && (
          <>
            <FileDeleteConfirmationModal
              isOpen={mediaFileModalOpen}
              onClick={handleDeleteFile}
              onClose={() => {
                setMediaFileModalOpen(false);
              }}
            />
            <HeaderContainer>
              <Heading2>
                <HeaderEntityContainer>
                  <HeaderEntityValue>Search results</HeaderEntityValue>
                </HeaderEntityContainer>
              </Heading2>
              <HeaderAction>
                {activeFile !== null ? <DangerButton onClick={handleDeleteClick}>Delete File</DangerButton> : null}
                {activeFile && props?.onInsertClick && (
                  <Button
                    primary={true}
                    onClick={() => {
                      props.onInsertClick?.(
                        `${activeFile?.webMediaFolder?.breadcrumb!.join('/')}/${activeFile.fileName}`
                      );
                    }}
                  >
                    Insert
                  </Button>
                )}
              </HeaderAction>
            </HeaderContainer>

            <MediaFileList>
              {searchResults?.map((result) => {
                return (
                  <MediaFileEntry
                    key={`media_file_entry_${result?.node?.id}`}
                    webMediaFolder={result?.node?.webMediaFolder}
                    mediaFile={result?.node as IWebMediaFile}
                    activeFile={activeFile}
                    onClick={onClick}
                  />
                );
              })}
            </MediaFileList>
          </>
        )}
      </div>
      <FluentModal isOpen={modalOpen}>
        <BasicHeader>{'Add Folder'}</BasicHeader>
        <Form.FormContainer>
          <Fieldset cols={1}>
            <BlurTextField
              label="Folder Name"
              value={newFolderName}
              onChange={setNewFolderName}
              enableOnChange
              data-testid="addNewFolder"
            />
          </Fieldset>
        </Form.FormContainer>
        <ModalBottomButtonContainer>
          <Button
            data-testid="globalAmendModal-cancel"
            onClick={() => {
              setModalOpen(false);
            }}
          >
            Cancel
          </Button>
          <SuccessButton onClick={onNewFolderClick} data-testid="globalAmendModal-amend">
            Save
          </SuccessButton>
        </ModalBottomButtonContainer>
      </FluentModal>
    </div>
  );
};

export default WebMediaFolderTreespace;
